import React, {Fragment, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Button, Box, Stack, Tab, Typography} from '@mui/material';
import {useTheme} from 'styled-components';

import {RefundStatusEnum} from 'domain/usecases';
import {Drawer} from 'presentation/components/Drawer';
import {Timeline} from 'presentation/components/Timeline';
import {Loading} from 'presentation/components/Loading/Loading';
import {ErrorState, TagChip} from 'presentation/components';
import {FeaturesEnum, OperationStateTypesEnum, RefundEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {translations} from 'presentation/translations-new';
import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {RequestErrorModal} from '../../../../components/RequestErrorModal';
import {RefundStatusLoadingState} from '../../../../components/RefundStatusLoadingState';
import {useGetRefundPdf} from '../../../../hooks/useGetRefundPdf';
import {useProtocolStatus} from '../../../../queries/useProtocolStatus';
import type {RefundFlowNavigationState} from '../../../../types/refundFlowNavigationState';
import {RefundDetailsTabGrid} from './components/RefundDetailsTabGrid';
import {RefundStatusDrawerV2Props} from './types';
import {StyledBoxTitle, StyledTabs} from './styles';

const DOWNLOAD_STATEMENT_STATUSES: RefundStatusEnum[] = [
	RefundStatusEnum.APPROVED_7,
	RefundStatusEnum.CONCLUDED_8,
	RefundStatusEnum.DENIED_5,
];

const {failToLoadTitle, failToLoadDescription, protocol, seeRequest} =
	translations['pt-br'].commons;
const {
	drawerDetailsPlaceholder,
	drawerDetailsTab,
	drawerFooterDownloadRefundStatement,
	drawerFooterSendAdditionalDocuments,
	drawerHistoryTab,
	drawerTitle,
} = translations['pt-br'].privateFlow.consultRefund;

function RefundStatusDrawerV2({
	title,
	protocolCode,
	chip,
	refund,
	openDrawer,
	onOpenDrawer,
	onCloseDrawer,
}: RefundStatusDrawerV2Props) {
	const theme = useTheme();
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState(0);
	const {isEnabled: isDirectAdditionalDocsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_ADDITIONAL_DOCS_REFUND_REANALYSIS,
	);
	const {isEnabled: isViewSupplementaryDocumentsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_VIEW_SUPPLEMENTARY_DOCUMENTS,
	);

	const {
		checkRequest,
		downloadPdf,
		CHECK_REQUEST_IS_ERROR,
		CHECK_REQUEST_IS_LOADING,
		DOWNLOAD_PDF_IS_ERROR,
		DOWNLOAD_PDF_IS_LOADING,
		setCheckRequestOperationState,
		setDownloadPdfOperationState,
	} = useGetRefundPdf();

	let secondaryCta: 'downloadStatement' | 'viewRequest' | null = null;

	if (refund) {
		if (DOWNLOAD_STATEMENT_STATUSES.includes(refund.situationCode)) {
			secondaryCta = 'downloadStatement';
		} else {
			secondaryCta = 'viewRequest';
		}
	}

	function handleCloseDrawer() {
		setActiveTab(0);
		onCloseDrawer();
	}

	function handleOpenDrawer() {
		setActiveTab(0);
		onOpenDrawer();
	}

	function handleSendAdditionalDocuments() {
		handleCloseDrawer();

		const canGoDirectToSearchProtocol =
			isDirectAdditionalDocsEnabled && isViewSupplementaryDocumentsEnabled;

		if (canGoDirectToSearchProtocol && refund?.protocol) {
			const navigationState: RefundFlowNavigationState = {
				additionalDocsReference: refund.protocol,
				refundTermsReturnTo: RefundEnum.CONSULT,
			};
			navigate(RefundEnum.SENDING_DOCUMENTS, {state: navigationState});
			return;
		}

		navigate(RefundEnum.TERMS_ADDITIONAL_DOCS);
	}

	async function handleDownloadStatement() {
		if (!refund?.protocol) return;
		await checkRequest(refund.protocol);
	}

	async function handleViewRequest() {
		if (!refund?.protocol) return;
		await downloadPdf(refund.protocol);
	}

	const showHistoryFooter = activeTab === 0 && refund != null;

	const {
		data: timeline,
		isLoading,
		isError,
		isSuccess,
		refetch,
	} = useProtocolStatus(protocolCode);

	const detailsTabContent = refund ? (
		<RefundDetailsTabGrid refund={refund} />
	) : (
		<Typography variant="body2" color="text.secondary">
			{drawerDetailsPlaceholder}
		</Typography>
	);

	return (
		<Fragment>
			<Drawer.Root
				open={openDrawer}
				onClose={handleCloseDrawer}
				onOpen={handleOpenDrawer}
				customMaxHeight="100%"
				height="90%"
				>
				<Fragment>
					<Drawer.Header
						onClose={handleCloseDrawer}
						title={drawerTitle}
						closeBtnDataTagId={
							RefundTaggingEnum.CLICK_ICONE_FECHAR_TRILHA_CONSULTAR_REEMBOLSO
						}
					/>
					<Drawer.Content sx={{display: 'flex', flexDirection: 'column', gap: 0, pt: 0}}>
						<StyledTabs
							value={activeTab}
							onChange={(_, value: number) => setActiveTab(value)}
							variant="fullWidth">
							<Tab label={drawerHistoryTab} />
							<Tab label={drawerDetailsTab} />
						</StyledTabs>
						{activeTab === 0 && isError ? (
							<Box pt={2}>
								<ErrorState
									title={failToLoadTitle}
									description={failToLoadDescription}
									handleRefresh={refetch}
								/>
							</Box>
						) : (
							<Stack flex={1} overflow="auto" pt={2} gap={2}>
								{activeTab === 0 ? (
									<Fragment>
										{isLoading ? <RefundStatusLoadingState /> : null}
										{isSuccess ? (
											<Fragment>
												<StyledBoxTitle>
													<Typography
														variant="h6"
														color={theme.palette.primary.dark}>
														<Box component="span" fontWeight={400}>
															{protocol}
														</Box>{' '}
														{title}
													</Typography>
													<TagChip
														label={chip.chipText}
														color={chip.chipVariant}
													/>
													<Typography variant="body2">
														{timeline.subtitle}
													</Typography>
												</StyledBoxTitle>
												{timeline.data && timeline.data.length > 0 ? (
													<Timeline content={timeline.data} />
												) : null}
											</Fragment>
										) : null}
									</Fragment>
								) : (
									detailsTabContent
								)}
							</Stack>
						)}
					</Drawer.Content>
					{showHistoryFooter ? (
						<Drawer.Footer hideFirstButton hideSecondButton>
							<Stack direction={{xs: 'column', sm: 'row'}} gap={2} width="100%">
								{refund?.situationCode === RefundStatusEnum.DENIED_5 ? (
									<Button
										variant="outlined"
										fullWidth
										size="large"
										onClick={handleSendAdditionalDocuments}
										data-tag-id={
											RefundTaggingEnum.CLICK_ENVIO_DE_DOCS_COMPLEMENTARES_REEMBOLSO
										}>
										{drawerFooterSendAdditionalDocuments}
									</Button>
								) : null}
								{secondaryCta === 'downloadStatement' ? (
									<Button
										variant="outlined"
										fullWidth
										size="large"
										disabled={CHECK_REQUEST_IS_LOADING}
										onClick={() => void handleDownloadStatement()}
										data-tag-id={
											RefundTaggingEnum.CLICK_DOWNLOAD_DEMONSTRATIVO_REEMBOLSO
										}
										endIcon={
											CHECK_REQUEST_IS_LOADING ? (
												<Loading size={16} color="inherit" />
											) : null
										}>
										{drawerFooterDownloadRefundStatement}
									</Button>
								) : (
									<Button
										variant="outlined"
										fullWidth
										size="large"
										disabled={DOWNLOAD_PDF_IS_LOADING}
										onClick={() => void handleViewRequest()}
										data-tag-id={
											RefundTaggingEnum.CLICK_VER_SOLICITACAO_CONSULTA_REEMBOLSO
										}
										endIcon={
											DOWNLOAD_PDF_IS_LOADING ? (
												<Loading size={16} color="inherit" />
											) : null
										}>
										{seeRequest}
									</Button>
								)}
							</Stack>
						</Drawer.Footer>
					) : null}
				</Fragment>
			</Drawer.Root>
			<RequestErrorModal
				open={DOWNLOAD_PDF_IS_ERROR}
				setOpen={() => setDownloadPdfOperationState(OperationStateTypesEnum.NONE)}
			/>
			<RequestErrorModal
				open={CHECK_REQUEST_IS_ERROR}
				setOpen={() => setCheckRequestOperationState(OperationStateTypesEnum.NONE)}
			/>
		</Fragment>
	);
}

export {RefundStatusDrawerV2};
